import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';
import { useShotSettingsStore } from '@/store/shotSettingsStore';
import { useCopyBoardStore } from '@/store/copyBoardStore';

export interface ShotSettingsValues {
  shotNotes: string;
  isPosterShot: boolean;
  sceneFloorIsTransparent: boolean;
  sizeTypes: string[];
  movementTypes: string[];
  angleTypes: string[];
  equipmentTypes: string[];
  framingTypes: string[];
  focusTypes: string[];
  frameRateTypes: string[];
  shotNumber: string;
  shotName: string;
  shotDuration: string;
  postProcessingKey: string;
  // NOTE: the canvas Shot List header strings (headerMetaSummary / headerLensLabel) live on
  // ShotSettingsPayloadData, as siblings of `title` — NOT here. ShotSettingsPayloadBuilder emits
  // them there unconditionally, and they are display-only: handleDone never sends them back, so
  // they are not part of this round-tripped value set.
}
export interface ShotMetadataOption { value: string; label: string; abbrev?: string; available?: boolean; group?: string }
export interface ShotSettingsPayloadData {
  shotId: number;
  title: string;
  values: ShotSettingsValues;
  /** Canvas Shot List parity. The METADATA segment only ("FS / Dolly zoom out / KL"), "" when the
   *  shot carries no size/movement/angle. NEVER the custom name — pair it with `values.shotName`
   *  so a name edited in the header drawer wins immediately, without a Unity round trip. Arrives
   *  pre-joined (categories with " / ", values inside a category with a bare "-"): render it
   *  verbatim, never re-split or re-case it. Additive — older hosts omit it. */
  headerMetaSummary?: string;
  /** The lens segment exactly as the canvas tile renders it ("29 mm"). A shot with NO camera row
   *  reads "0 mm", not "" — the canvas prints that and exact parity is the point (see
   *  ShotHeaderLabelBuilder.GetLensLabelForShotId). "" only reaches here from a host that omits
   *  the field, and then the segment and its separator are both dropped. Additive. */
  headerLensLabel?: string;
  options: Record<'size' | 'movement' | 'angle' | 'equipment' | 'framing' | 'focus' | 'frameRate' | 'postProcessing', ShotMetadataOption[]>;
  flags: { isAnimatic: boolean; posterLocked: boolean; floorTransparencyLocked: boolean; numberingLocked: boolean };
  // Not populated by Unity in slice 1 (no validation errors to reject on yet). Declared now so the
  // board's echo-resolution effect can mirror Scene Settings' `if (!data.error)` close guard —
  // absent/undefined is falsy, so it always closes on success until a real error lands here.
  error?: { field: string; messageKey: string } | null;
  // Additive: Unity always emits it, but older echoes/tests may omit it.
  audioClipCount?: number;
  /** The project's DocumentFrameRate value (30/25/24), from DocumentSettingsManager.GetFrameRateValue().
   *  Governs the SS:FF (seconds:frames) interpretation of the duration boxes — the same value Unity's
   *  own duration parser/clamp uses (DocumentManager.ApplyShotSettingsFromWeb). Additive: older hosts
   *  omit it, in which case the modal falls back to Unity's own default of 30. */
  projectFrameRate?: number;
}

export interface SetShotSettingsMessage {
  type: 'setShotSettings';
  payload: ShotSettingsPayloadData;
}
export interface ShotDeletedPayload {
  shotId: number;
  sceneId: number;
  error: { messageKey: string } | null;
}
export interface ShotDeletedMessage {
  type: 'shotDeleted';
  payload: ShotDeletedPayload;
}

/** Ack for the board context menu's copy/insert/paste verbs and the Add-shot tile's `addShotToScene`.
 *
 * One verb with an `op` discriminator rather than four, to keep ALL_BRIDGE_TYPES small.
 * `shotId` is the NEW shot on success and the REQUESTED shot on failure — callers match on `op`
 * plus "this is the ack for the mutation I just started" (clear before send), never on shotId,
 * because on success the new id is exactly what they are waiting to be told.
 *
 * `add` is the exception to "the REQUESTED shot on failure": the Add tile names no shot, so a
 * failed add carries -1 (Unity's IllegalRecordId). Callers ignore the id on an error ack. */
/**
 * 20260909 — the two BATCH ops board select mode adds (spec item 2, wire contract). They ride the
 * same `shotMutated` envelope rather than earning verbs of their own for the reason the four above
 * share one: ALL_BRIDGE_TYPES stays small, and every caller already matches on `op`.
 *  · 'copyMany'      — the ack for `copyShots`. `shotId` is the FIRST copied id (-1 on failure),
 *                      `count` how many were armed. Mutates nothing, so there is no repush.
 *  · 'duplicateMany' — the ack for `duplicateShots`, sent AFTER the single repush. `shotIds` are
 *                      ALL the new ids in creation order and `shotId` is the first of them (-1 with
 *                      `duplicateManyFailed` when nothing was created).
 * A batch PASTE keeps `op: 'paste'` — the single-paste waiter and its select-the-first behaviour are
 * unchanged — and simply gains `shotIds`.
 */
export type ShotMutationOp = 'copy' | 'insert' | 'paste' | 'add' | 'copyMany' | 'duplicateMany';
export interface ShotMutatedPayload {
  op: ShotMutationOp;
  shotId: number;
  sceneId: number;
  error: { messageKey: string } | null;
  /** Batch ops only: every new shot id, in creation order. Also carried by a batch `paste`.
   *  Additive — a single-shot ack omits it, and callers fall back to `shotId`. */
  shotIds?: number[];
  /** `copyMany` only: how many shots were actually armed on Unity's copy board. */
  count?: number;
}
export interface ShotMutatedMessage {
  type: 'shotMutated';
  payload: ShotMutatedPayload;
}

/**
 * The batch-delete ack (20260909). A message type of its own rather than an `op` on `shotDeleted`,
 * because its payload is a different shape: it reports a LIST of ids that went and a COUNT of ids
 * Unity refused, where the single delete reports one id and one error.
 *
 * `skipped` counts the shots Unity REFUSED — an id that named no row, a hide that failed, a throw.
 * (20260909, batch 11 item 1: it no longer means "kept because a scene must keep one shot" — a
 * selection covering a whole scene now deletes that scene, so nothing is kept back.) It is not an
 * error: the batch still succeeded for everything else, and the web reports the difference as a
 * warning toast rather than as a failure.
 *
 * `deleted` may legitimately be EMPTY (every id skipped) — the ack is still sent, because it is what
 * releases the web's busy scrim.
 *
 * `deletedSceneIds` (20260909, batch 11 item 1) names the scenes Unity took WHOLE, because every one
 * of their visible shots was in the request. Optional: an older host omits it, which reads as "no
 * scene left" — the same shape this ack had before, so the caller simply waits on nothing extra.
 * Its shots are in `deleted` as usual, so the two gates are complementary, not alternatives.
 */
export interface ShotsDeletedPayload {
  deleted: number[];
  deletedSceneIds?: number[];
  skipped: number;
  error: { messageKey: string } | null;
}
export interface ShotsDeletedMessage {
  type: 'shotsDeleted';
  payload: ShotsDeletedPayload;
}

export interface CopyBoardStatePayload {
  hasShot: boolean;
}
export interface CopyBoardStateMessage {
  type: 'copyBoardState';
  payload: CopyBoardStatePayload;
}

/** A render of one shot with a CANDIDATE colour grade — the answer to the Shot Details grade picker,
 *  which until now sat under the board's cached tile and so showed the grade the shot already had.
 *  `postProcessingKey` is the key the request asked for, echoed back untouched: Unity resolves an
 *  empty/inherit pick up the scene → project ladder and renders with whatever that lands on, so the
 *  key it RENDERED with is usually a different string and would fill a key nobody is reading.
 *
 *  20260902 - `floorTransparent` rides the same echo for the same reason, and is the CANDIDATE the
 *  request carried, not what Unity resolved: the sheet caches under what it asked for. Additive —
 *  an older host omits it, which reads as false and lands on the same key that host's requests fill. */
export interface ShotGradePreviewPayloadData {
  shotId: number;
  postProcessingKey: string;
  imageUrl: string;
  floorTransparent?: boolean;
}
export interface SetShotGradePreviewMessage {
  type: 'setShotGradePreview';
  payload: ShotGradePreviewPayloadData;
}

/** Cache key for shotSettingsStore.gradePreviews. Per-shot, because the render is of that one shot.
 *
 *  20260902 - the floor component makes the transparent-floor checkbox a preview input alongside the
 *  grade: without it a toggle could neither invalidate the landed render nor arm a new request, and
 *  the sheet's thumb (which OUTRANKS the board tile once any preview has landed) would keep painting
 *  the opaque-floor image. DEFAULTED so every caller that has no floor to offer keys as before. */
export function shotGradePreviewKey(shotId: number, postProcessingKey: string, sceneFloorIsTransparent = false): string {
  return `${shotId}|${postProcessingKey}|${sceneFloorIsTransparent ? 1 : 0}`;
}

export type ShotSettingsMessage =
  | SetShotSettingsMessage
  | ShotDeletedMessage
  | ShotsDeletedMessage
  | ShotMutatedMessage
  | CopyBoardStateMessage
  | SetShotGradePreviewMessage;

export const shotSettingsHandlers = {
  setShotSettings: (msg: SetShotSettingsMessage) => {
    if (msg.payload && typeof msg.payload.shotId === 'number') {
      useShotSettingsStore.getState().setCurrent(msg.payload);
    }
  },
  shotDeleted: (msg: ShotDeletedMessage) => {
    if (msg.payload && typeof msg.payload.shotId === 'number') {
      useShotSettingsStore.getState().setDeleteResult(msg.payload);
    }
  },
  shotsDeleted: (msg: ShotsDeletedMessage) => {
    // The ARRAY is the discriminator, not `skipped`: a batch where every id was refused still sends
    // `deleted: []`, and that ack is what releases the caller's scrim.
    if (msg.payload && Array.isArray(msg.payload.deleted)) {
      useShotSettingsStore.getState().setDeleteManyResult(msg.payload);
    }
  },
  shotMutated: (msg: ShotMutatedMessage) => {
    if (msg.payload && typeof msg.payload.op === 'string') {
      useShotSettingsStore.getState().setMutateResult(msg.payload);
    }
  },
  copyBoardState: (msg: CopyBoardStateMessage) => {
    if (msg.payload && typeof msg.payload.hasShot === 'boolean') {
      useCopyBoardStore.getState().setHasShot(msg.payload.hasShot);
    }
  },
  setShotGradePreview: (msg: SetShotGradePreviewMessage) => {
    if (!msg.payload || typeof msg.payload.shotId !== 'number' || typeof msg.payload.imageUrl !== 'string') return;
    const { shotId, postProcessingKey, imageUrl } = msg.payload;
    const key = shotGradePreviewKey(shotId, postProcessingKey, !!msg.payload.floorTransparent);
    useShotSettingsStore.setState((s) => ({ gradePreviews: { ...s.gradePreviews, [key]: imageUrl } }));
  },
} satisfies HandlerMap;

export interface ApplyShotSettingsPayload extends ShotSettingsValues {
  shotId: number;
}

export function sendRequestShotSettings(shotId: number): void {
  enqueueSendToNative('requestShotSettings', { shotId });
}
export function sendApplyShotSettings(payload: ApplyShotSettingsPayload): void {
  enqueueSendToNative('applyShotSettings', { ...payload });
}
export function sendDeleteShot(shotId: number): void {
  enqueueSendToNative('deleteShot', { shotId });
}

/** Ask Unity to render `shotId` with `postProcessingKey` as its grade and `sceneFloorIsTransparent` as
 *  its floor. Read-only: neither pick is persisted until the sheet's Done sends it on
 *  `applyShotSettings`, and this render deliberately never reaches the thumbnail cache — a candidate
 *  filed under the shot's unchanged database hash would be served as the real tile forever after.
 *
 *  No debounce for the same reason the project-settings preview needs none: both inputs are discrete
 *  controls (a dropdown and a checkbox) and the caller's cache-key gate collapses repeats.
 *
 *  20260902 - the floor goes out as a STRING tri-state ("true"/"false", absent = "use the persisted
 *  row") because Unity parses these with JsonUtility, which cannot tell an absent bool from `false`:
 *  a plain bool would make every host that omits the field render an OPAQUE floor for a shot whose
 *  row says transparent, silently. Same shape SkyPreviewRequestDto's optional candidates use. */
export function sendRequestShotGradePreview(shotId: number, postProcessingKey: string, sceneFloorIsTransparent: boolean): void {
  enqueueSendToNative('requestShotGradePreview', { shotId, postProcessingKey, floorTransparent: sceneFloorIsTransparent ? 'true' : 'false' });
}

/** Arms Unity's copy board with this shot. Mutates no document state — paste re-reads the live
 * source row — so the only response is a fresh `copyBoardState` plus the `shotMutated` ack. */
export function sendCopyShot(shotId: number): void {
  enqueueSendToNative('copyShot', { shotId });
}
/** Duplicates `shotId` in place (right after itself). Clipboard-independent. */
export function sendInsertShot(shotId: number): void {
  enqueueSendToNative('insertShot', { shotId });
}
/** Pastes the copy board immediately after `shotId` — the TARGET tile, not the copied one.
 *
 *  20260909 — the VERB and its payload are unchanged, but the semantics widened with board select
 *  mode: Unity's copy board holds a LIST now, and this pastes every copied shot in copy order, each
 *  landing after the previous one and the first after the target. A one-shot copy board therefore
 *  behaves exactly as it always did. */
export function sendPasteShot(shotId: number): void {
  enqueueSendToNative('pasteShot', { shotId });
}

/** Arms Unity's copy board with a SET of shots (board select mode, 20260909). Ids go out in BOARD
 *  order — scene order, then shot order — because that is the order paste replays them in. Mutates
 *  no document state, exactly like the single copy: the answers are a fresh `copyBoardState` plus
 *  one `shotMutated` ack with `op: 'copyMany'`. */
export function sendCopyShots(shotIds: number[]): void {
  enqueueSendToNative('copyShots', { shotIds });
}
/** Duplicates each of `shotIds` in place (each new shot lands right after ITS OWN source).
 *  Clipboard-independent, the batch twin of sendInsertShot. Answered by ONE repush and then one
 *  `shotMutated` ack with `op: 'duplicateMany'` carrying every new id. */
export function sendDuplicateShots(shotIds: number[]): void {
  enqueueSendToNative('duplicateShots', { shotIds });
}
/** Deletes each of `shotIds`. 20260909 (batch 11 item 1): a scene whose EVERY visible shot is in the
 *  list is deleted whole and named in the ack's `deletedSceneIds`; `skipped` now counts only ids
 *  Unity refused outright.
 *
 *  `sceneIds` (20260909, review fix) carries INTENT, not a hint: it is the exact set of scenes the
 *  confirm told the user it would take, frozen with the id list. Unity deletes a scene whole only
 *  when it appears here AND its own live shot list agrees the request covers all of it — without
 *  this, Unity re-derives "whole" from a document the web may not have reconciled yet and could take
 *  a scene the confirm never mentioned. Always sent (possibly empty), so an empty array reads as
 *  "no scene may go whole". */
export function sendDeleteShots(shotIds: number[], sceneIds: number[]): void {
  enqueueSendToNative('deleteShots', { shotIds, sceneIds });
}
